// ─────────────────────────────────────────────
// Oike — Finanzas › Movimientos (pure; tested by scripts/test-expense-receipts.mjs). Agreed with Ale on 04-11-2026.
// A summary of what was REGISTERED in a period, per currency (EUR, USD, BOB, never added or converted). It is not a
// result, a profit or EBITDA, and it does not say how much money is in any account.
// - Income: the same financeSummary of Ingresos (each payment once, whatever its installments or applications).
// - Business payments: the confirmed payments of business expenses in the period, split by where the money came from
//   (business, personal, other). A business expense paid with personal money did not leave a business account: it is
//   counted once, in the business, and said so.
// - Personal expenses go apart, never added to the business.
// - Pending and overdue, and incomplete or to-review records, are listed with their records. A month without any
//   registered business expense is «sin gastos registrados», never a confirmed zero.
// Each figure carries the records behind it, to open them.
// ─────────────────────────────────────────────
const cents = (n) => Math.round(Number(n) * 100);
const money = (c) => Math.round(c) / 100;
const ORIGINS = ['business', 'personal', 'other'];

const monthsIn = (from, to) => {
  const out = [];
  let [y, m] = from.slice(0, 7).split('-').map(Number);
  const [ty, tm] = to.slice(0, 7).split('-').map(Number);
  while (y < ty || (y === ty && m <= tm)) { out.push(`${y}-${String(m).padStart(2, '0')}`); m++; if (m > 12) { m = 1; y++; } }
  return out;
};

/** One scope's expenses (views with their computed state) in a period: payments by origin, pending, overdue, review. */
export function expenseMovements(list = [], { from, to }) {
  const byCur = {};
  const slot = (c) => (byCur[c] = byCur[c] || {
    paid: { amount: 0, items: [] }, byOrigin: Object.fromEntries(ORIGINS.map(o => [o, { amount: 0, items: [] }])),
    pending: { amount: 0, items: [] }, overdue: { amount: 0, items: [] },
  });
  const review = [];
  const monthsWithExpenses = new Set();
  for (const x of list) {
    const s = x.state || {};
    if (s.state === 'cancelled') continue;
    if (x.incurredOn) monthsWithExpenses.add(x.incurredOn.slice(0, 7));
    const ref = { id: x.id, concept: x.concept, supplier: x.supplier, currency: x.currency };
    for (const p of s.payments || []) {
      if (p.paidOn) monthsWithExpenses.add(p.paidOn.slice(0, 7));
      if (p.status !== 'confirmed' || p.multi || p.otherCurrency) continue;
      if (p.paidOn < from || p.paidOn > to) continue;
      const b = slot(p.currency || x.currency);
      const origin = ORIGINS.includes(p.paidFrom) ? p.paidFrom : 'other';
      const item = { ...ref, paymentId: p.id, day: p.paidOn, amount: p.amount, paidFrom: origin, method: p.method || '' };
      b.paid.amount += cents(p.amount); b.paid.items.push(item);
      b.byOrigin[origin].amount += cents(p.amount); b.byOrigin[origin].items.push(item);
    }
    if (s.pending !== null && s.pending > 0) {
      const b = slot(x.currency);
      const item = { ...ref, amount: s.pending, dueOn: s.dueOn || '' };
      b.pending.amount += cents(s.pending); b.pending.items.push(item);
      if (s.overdue) { b.overdue.amount += cents(s.pending); b.overdue.items.push(item); }
    }
    const reasons = [...(s.review || [])];
    if (s.intent?.kind === 'other_currency' && !reasons.includes('payment_other_currency')) reasons.push('other_currency_notice');
    if (reasons.length) review.push({ ...ref, amount: x.amount, reasons });
  }
  for (const b of Object.values(byCur)) {
    for (const k of ['paid', 'pending', 'overdue']) b[k].amount = money(b[k].amount);
    for (const o of ORIGINS) b.byOrigin[o].amount = money(b.byOrigin[o].amount);
  }
  const monthsWithout = monthsIn(from, to).filter(m => !monthsWithExpenses.has(m));
  return { byCurrency: byCur, review, monthsWithout };
}

/**
 * The whole summary. income: the financeSummary output (or null with incomeError). business / personal: { list } or
 * { error }. Each part that could not be read says so; it is never shown as zero.
 */
export function movementsSummary({ income = null, incomeError = '', business = {}, personal = {}, from, to }) {
  const biz = business.error ? null : expenseMovements(business.list || [], { from, to });
  const per = personal.error ? null : expenseMovements(personal.list || [], { from, to });
  const currencies = [...new Set([...Object.keys(income?.byCurrency || {}), ...Object.keys(biz?.byCurrency || {})])].sort();
  const businessView = {};
  for (const c of currencies) {
    const inc = income?.byCurrency?.[c] || null;
    const out = biz?.byCurrency?.[c] || null;
    businessView[c] = {
      received: income ? (inc ? inc.received : { amount: 0, items: [] }) : null,
      refunds: income ? (inc ? inc.refunds : { amount: 0, items: [] }) : null,
      netReceived: income ? (inc ? inc.net : 0) : null,
      toCollect: income ? (inc ? inc.pending : { amount: 0, items: [] }) : null,
      overdueToCollect: income ? (inc ? inc.overdue : { amount: 0, items: [] }) : null,
      unapplied: income ? (inc ? inc.unapplied : { amount: 0, items: [] }) : null,
      paid: biz ? (out ? out.paid : { amount: 0, items: [] }) : null,
      byOrigin: biz ? (out ? out.byOrigin : null) : null,
      toPay: biz ? (out ? out.pending : { amount: 0, items: [] }) : null,
      overdueToPay: biz ? (out ? out.overdue : { amount: 0, items: [] }) : null,
      // Only when both parts were read: what came in minus what went out, as registered (never a result)
      difference: income && biz ? money(cents(inc ? inc.net : 0) - cents(out ? out.paid.amount : 0)) : null,
    };
  }
  return {
    from, to,
    business: {
      currencies: businessView,
      incomeError: income ? '' : (incomeError || 'not_read'), expensesError: biz ? '' : (business.error || 'not_read'),
      review: biz?.review || [], monthsWithout: biz?.monthsWithout || [],
      unresolvedPlans: income?.unresolvedPlans || [], legacyWithoutAmount: income?.legacyWithoutAmount || [],
    },
    personal: per ? { currencies: per.byCurrency, review: per.review, monthsWithout: per.monthsWithout, error: '' } : { currencies: {}, review: [], monthsWithout: [], error: personal.error || 'not_read' },
  };
}

export const REVIEW_REASON_TEXT = {
  overpaid: 'los pagos superan el importe', payment_multiple_links: 'un pago vinculado a varios gastos', payment_other_currency: 'un pago en otra moneda',
  unconfirmed_payment: 'un pago sin confirmar', incomplete_operation: 'operación incompleta (falta registrar el pago indicado)',
  same_key_conflict: 'misma operación con datos distintos', other_currency_notice: 'pago informado en otra moneda (no se cuenta)',
};
